import {
  BOT_BUILDS,
  HERO_ITEM_ROLE,
  ITEM_ROLES,
  ITEM_ORDER,
  ITEM_TIERS,
  ITEMS,
  SHOP,
  purchasePlan,
  sellPrice,
  type HeroState,
  type HeroId,
  type ItemPassive,
  type ItemRole,
  type ItemStats,
  type ItemTier,
  type Simulation,
} from '@moba/shared';
import { itemIconUrl } from './itemIcons.ts';
import { t } from './i18n.ts';

/** Cómo se describe cada estadística de un objeto. */
const STAT_TEXT: Record<keyof ItemStats, (v: number) => string> = {
  maxHp: (v) => `+${v} ${t('Vida')}`,
  hpRegen: (v) => `+${v.toFixed(1)} ${t('Regeneración/s')}`,
  attackDamage: (v) => `+${v} ${t('Daño')}`,
  attackSpeed: (v) => `+${Math.round(v * 100)}% ${t('Vel. ataque')}`,
  armor: (v) => `+${v} ${t('Armadura')}`,
  abilityHaste: (v) => `+${v} ${t('Acel. habilidad')}`,
  moveSpeed: (v) => `+${v.toFixed(1)} ${t('Vel. movimiento')}`,
  omnivamp: (v) => `+${Math.round(v * 100)}% ${t('Omnisucción')}`,
  lethality: (v) => `+${v} ${t('Letalidad')}`,
  armorPen: (v) => `+${Math.round(v * 100)}% ${t('Pen. armadura')}`,
  tenacity: (v) => `+${Math.round(v * 100)}% ${t('Tenacidad')}`,
};

const TIER_LABEL: Record<ItemTier, string> = {
  basic: 'Básicos',
  boots: 'Botas',
  epic: 'Épicos',
  legendary: 'Legendarios',
};

export const ROLE_LABEL: Record<ItemRole, string> = {
  fighter: 'Luchador',
  tank: 'Tanque',
  marksman: 'Tirador',
  assassin: 'Asesino',
};

type RoleFilter = ItemRole | 'all';

const TIER_TAG: Record<ItemTier, string> = {
  basic: 'BÁSICO',
  boots: 'BOTAS',
  epic: 'ÉPICO',
  legendary: 'LEGENDARIO',
};

export function describeItem(id: string): string {
  const item = ITEMS[id];
  if (!item) return '';
  return (Object.entries(item.stats) as Array<[keyof ItemStats, number]>).map(([k, v]) => STAT_TEXT[k](v)).join(' · ');
}

/** Datos del tooltip de un objeto (inventario del HUD). */
export function itemTooltipData(id: string): {
  title: string;
  subtitle: string;
  stats: Array<{ label: string; value: string }>;
  passive?: { name: string; text: string };
  note: string;
} | null {
  const item = ITEMS[id];
  if (!item) return null;
  const stats = (Object.entries(item.stats) as Array<[keyof ItemStats, number]>).map(([k, v]) => {
    const text = STAT_TEXT[k](v);
    const space = text.indexOf(' ');
    return { label: text.slice(space + 1), value: text.slice(0, space) };
  });
  return {
    title: t(item.name),
    subtitle: `${t(TIER_TAG[item.tier])} · ${item.cost}`,
    stats,
    passive: item.passive ? { name: t(item.passive.name), text: describePassive(item.passive) } : undefined,
    note: t('Se vende por {gold} de oro en la tienda.', { gold: sellPrice(id) }),
  };
}

/** Texto del pasivo de un objeto. */
export function describePassive(passive: ItemPassive): string {
  switch (passive.kind) {
    case 'onHit':
      return t('Tus ataques básicos infligen {value} de daño adicional.', { value: passive.value });
    case 'thorns':
      return t('Devuelve el {value}% del daño que recibes de héroes y minions.', { value: Math.round(passive.value * 100) });
    case 'takedownHeal':
      return t('Al matar o asistir a un héroe recuperas el {value}% de tu vida máxima.', { value: Math.round(passive.value * 100) });
    case 'structureDamage':
      return t('Infliges un {value}% más de daño a torres y nexos.', { value: Math.round(passive.value * 100) });
    case 'minionGold':
      return t('Rematar un minion te da {value} de oro extra.', { value: passive.value });
    case 'minionDamage':
      return t('Infliges un {value}% más de daño a los minions.', { value: Math.round(passive.value * 100) });
    case 'maxHpOnHit':
      return t('Tus ataques básicos infligen además el {value}% de la vida máxima del objetivo.', { value: Math.round(passive.value * 100) });
    case 'comboFinisher':
      return t('El tercer golpe de tu combo básico inflige un {value}% más de daño.', { value: Math.round(passive.value * 100) });
    case 'heroDamage':
      return t('Infliges un {value}% más de daño a los héroes.', { value: Math.round(passive.value * 100) });
    case 'ambush':
      return t('Infliges un {value}% más de daño a héroes con más del 80% de vida.', { value: Math.round(passive.value * 100) });
    case 'cooldownOnHit':
      return t('Cada ataque básico reduce {value} s los enfriamientos de Q, W y E.', { value: passive.value });
    case 'takedownCooldown':
      return t('Al matar o asistir a un héroe, tus enfriamientos restantes bajan un {value}%.', { value: Math.round(passive.value * 100) });
    case 'splash':
      return t('Tus ataques básicos cuerpo a cuerpo infligen el {value}% del daño a los enemigos cercanos al objetivo.', { value: Math.round(passive.value * 100) });
    case 'laneResist':
      return t('Recibes un {value}% menos de daño de minions y torres.', { value: Math.round(passive.value * 100) });
    case 'lifeline':
      return t('Al bajar del 30% de vida obtienes un escudo del {value}% de tu vida máxima durante 3 s (cada 60 s).', { value: Math.round(passive.value * 100) });
    case 'execute':
      return t('Infliges un {value}% más de daño a enemigos por debajo del 30% de vida.', { value: Math.round(passive.value * 100) });
  }
}

function tooltip(id: string): string {
  const item = ITEMS[id];
  if (!item) return '';
  const passive = item.passive ? `\n${t(item.passive.name)}: ${describePassive(item.passive)}` : '';
  return `${t(item.name)} (${item.cost})\n${describeItem(id)}${passive}`;
}

/** Icono de objeto: su sprite (o la abreviatura sobre su color si falta). */
export function itemIcon(id: string, el: HTMLElement): void {
  const item = ITEMS[id];
  const art = itemIconUrl(id);
  el.textContent = item && !art ? item.short : '';
  el.style.background = !item
    ? ''
    : art
      ? `center / 82% no-repeat url("${art}"), radial-gradient(circle at 35% 30%, ${item.color}55, #0d1520 75%)`
      : `linear-gradient(140deg, ${item.color}, #1a2533)`;
  el.title = item ? tooltip(id) : '';
}

const h = <K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] => {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text !== undefined) el.textContent = text;
  return el;
};

/**
 * Tienda (tecla I o botón del HUD), al estilo de LoL: catálogo por niveles a la
 * izquierda; a la derecha el objeto elegido con su receta (✓ lo que ya tienes),
 * en qué se convierte y el precio real descontando tus componentes. Clic =
 * elegir, clic derecho o doble clic = comprar. Se compra en la base o muerto;
 * clic en un objeto del inventario lo vende por el 70%.
 */
export class Shop {
  private readonly root = document.getElementById('shop') as HTMLElement;
  private readonly goldEl = document.getElementById('shop-gold') as HTMLElement;
  private readonly statusEl = document.getElementById('shop-status') as HTMLElement;
  private readonly tiles = new Map<string, { root: HTMLButtonElement; price: HTMLElement }>();
  private readonly sections = new Map<ItemTier, HTMLElement>();
  private readonly roleButtons = new Map<RoleFilter, HTMLButtonElement>();
  private readonly buildStrip = h('div', 'shop-build');
  private role: RoleFilter = 'all';
  private heroId?: HeroId;
  private itemsKey = '';
  private readonly inventory: HTMLButtonElement[] = [];
  private readonly detail = h('aside', 'shop-detail');
  private selected = ITEM_ORDER.find((id) => ITEMS[id].tier === 'legendary') ?? ITEM_ORDER[0];
  /** firma del último estado pintado del detalle (para no rehacer el DOM cada frame) */
  private detailKey = '';
  private lastPlayer?: HeroState;
  private lastCanShop = false;

  constructor(
    private readonly onBuy: (itemId: string) => void,
    private readonly onSell: (slot: number) => void,
  ) {
    const grid = document.getElementById('shop-items') as HTMLElement;
    grid.replaceChildren();
    const catalog = h('div', 'shop-catalog');
    // filtro por rol: el de tu héroe por defecto (★), todos o cualquier otro
    const roles = h('div', 'shop-roles');
    for (const role of [...ITEM_ROLES, 'all'] as RoleFilter[]) {
      const button = h('button', 'shop-role', role === 'all' ? t('Todos') : t(ROLE_LABEL[role]));
      button.type = 'button';
      button.addEventListener('click', () => this.setRole(role));
      roles.append(button);
      this.roleButtons.set(role, button);
    }
    catalog.append(roles, this.buildStrip);
    for (const tier of ITEM_TIERS) {
      const section = h('section', `shop-tier tier-${tier}`);
      this.sections.set(tier, section);
      section.append(h('h3', undefined, t(TIER_LABEL[tier])));
      const tiles = h('div', 'shop-tiles');
      for (const id of ITEM_ORDER.filter((itemId) => ITEMS[itemId].tier === tier)) {
        const tile = h('button', 'shop-tile');
        tile.type = 'button';
        const icon = h('span', 'item-icon');
        itemIcon(id, icon);
        const price = h('span', 'tile-price', String(ITEMS[id].cost));
        tile.append(icon, price);
        tile.addEventListener('click', () => this.select(id));
        tile.addEventListener('dblclick', () => this.onBuy(id));
        tile.addEventListener('contextmenu', (event) => {
          event.preventDefault();
          this.select(id);
          this.onBuy(id);
        });
        tiles.append(tile);
        this.tiles.set(id, { root: tile, price });
      }
      section.append(tiles);
      catalog.append(section);
    }
    grid.append(catalog, this.detail);

    const inv = document.getElementById('shop-inventory') as HTMLElement;
    for (let i = 0; i < SHOP.slots; i++) {
      const slot = document.createElement('button');
      slot.className = 'inv-slot';
      slot.addEventListener('click', () => this.onSell(i));
      inv.appendChild(slot);
      this.inventory.push(slot);
    }
    document.getElementById('shop-close')?.addEventListener('click', () => this.setOpen(false));
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  setOpen(open: boolean): void {
    this.root.hidden = !open;
  }

  toggle(): void {
    this.setOpen(!this.isOpen);
  }

  /** Filtra el catálogo por rol ('all' = todo). */
  private setRole(role: RoleFilter): void {
    this.role = role;
    for (const [key, button] of this.roleButtons) button.classList.toggle('active', key === role);
    for (const [id, tile] of this.tiles) tile.root.hidden = role !== 'all' && !ITEMS[id].roles.includes(role);
    for (const [tier, section] of this.sections) {
      section.hidden = ITEM_ORDER.every((id) => ITEMS[id].tier !== tier || this.tiles.get(id)?.root.hidden);
    }
    // la build recomendada es la del rol elegido (o la de tu héroe con "Todos")
    const buildRole = role === 'all' ? (this.heroId ? HERO_ITEM_ROLE[this.heroId] : 'fighter') : role;
    const strip: HTMLElement[] = [h('span', 'shop-build-label', t('Build recomendada · {role}', { role: t(ROLE_LABEL[buildRole]) }))];
    for (const id of BOT_BUILDS[buildRole]) strip.push(this.chip(id, Boolean(this.lastPlayer?.items.includes(id))));
    this.buildStrip.replaceChildren(...strip);
    if (!ITEMS[this.selected].roles.includes(buildRole) && role !== 'all') this.select(BOT_BUILDS[buildRole][1] ?? BOT_BUILDS[buildRole][0]);
  }

  private select(id: string): void {
    this.selected = id;
    this.detailKey = '';
    if (this.lastPlayer) this.renderDetail(this.lastPlayer, this.lastCanShop);
  }

  update(sim: Simulation, player: HeroState): void {
    if (!this.isOpen) return;
    const canShop = sim.canShop(player);
    this.lastPlayer = player;
    const itemsKey = player.items.join(',');
    if (itemsKey !== this.itemsKey && player.heroId === this.heroId) {
      // refresca los ✓ de la build recomendada
      this.itemsKey = itemsKey;
      this.setRole(this.role);
    }
    if (player.heroId !== this.heroId) {
      this.itemsKey = itemsKey;
      // otro héroe: abre en su rol, con su rol marcado con ★
      this.heroId = player.heroId;
      const mine = HERO_ITEM_ROLE[player.heroId];
      for (const [key, button] of this.roleButtons) {
        button.textContent = key === 'all' ? t('Todos') : `${key === mine ? '★ ' : ''}${t(ROLE_LABEL[key])}`;
      }
      this.setRole(mine);
    }
    this.lastCanShop = canShop;
    this.goldEl.textContent = String(player.gold);
    const full = player.items.length >= SHOP.slots;
    this.statusEl.textContent = !canShop
      ? t('Vuelve a tu base para comprar (B para volver)')
      : full
        ? t('Inventario lleno: completa un objeto o vende otro')
        : document.body.classList.contains('touch')
          ? t('Estás en la tienda · toca un objeto y pulsa comprar')
          : t('Estás en la tienda · clic derecho para comprar');
    this.statusEl.classList.toggle('warn', !canShop);

    for (const [id, tile] of this.tiles) {
      const plan = purchasePlan(player.items, id);
      const cost = plan?.cost ?? ITEMS[id].cost;
      const buyable = Boolean(canShop && plan && !plan.blocked && player.gold >= cost);
      tile.price.textContent = String(cost);
      tile.root.classList.toggle('discounted', cost < ITEMS[id].cost);
      tile.root.classList.toggle('unaffordable', player.gold < cost);
      tile.root.classList.toggle('buyable', buyable);
      tile.root.classList.toggle('owned', player.items.includes(id));
      tile.root.classList.toggle('selected', id === this.selected);
      tile.root.classList.toggle('blocked', Boolean(plan?.blocked));
    }
    this.renderDetail(player, canShop);

    this.inventory.forEach((slot, i) => {
      const id = player.items[i];
      if (id) {
        itemIcon(id, slot);
        slot.title = `${t('{item} — vender por {gold}', { item: t(ITEMS[id].name), gold: sellPrice(id) })}\n${describeItem(id)}`;
        slot.disabled = !canShop;
      } else {
        slot.textContent = '';
        slot.style.background = '';
        slot.title = '';
        slot.disabled = true;
      }
    });
  }

  /** Detalle del objeto elegido: receta, en qué se usa y botón de compra. */
  private renderDetail(player: HeroState, canShop: boolean): void {
    const id = this.selected;
    const item = ITEMS[id];
    const plan = purchasePlan(player.items, id);
    const key = `${id}|${player.items.join(',')}|${player.gold}|${canShop}`;
    if (key === this.detailKey) return;
    this.detailKey = key;
    if (!item || !plan) return;

    const head = h('div', 'detail-head');
    const icon = h('span', 'item-icon big');
    itemIcon(id, icon);
    const titles = h('div', 'detail-titles');
    titles.append(h('div', `detail-tag tag-${item.tier}`, t(TIER_TAG[item.tier])), h('div', 'detail-name', t(item.name)));
    const roleLine = h('div', 'detail-roles', item.roles.length === ITEM_ROLES.length ? t('Para todos los roles') : item.roles.map((role) => t(ROLE_LABEL[role])).join(' · '));
    titles.append(roleLine);
    head.append(icon, titles);

    const stats = h('ul', 'detail-stats');
    for (const [k, v] of Object.entries(item.stats) as Array<[keyof ItemStats, number]>) stats.append(h('li', undefined, STAT_TEXT[k](v)));

    const parts: HTMLElement[] = [head, stats];
    if (item.passive) {
      const passive = h('p', 'detail-passive');
      passive.append(h('b', undefined, `${t('Único')} · ${t(item.passive.name)}: `), document.createTextNode(describePassive(item.passive)));
      parts.push(passive);
    }
    if (item.unique || item.group) {
      parts.push(h('p', 'detail-note', item.group ? t('Sólo puedes llevar un par de botas.') : t('Objeto único: sólo uno por inventario.')));
    }

    // receta: el objeto arriba, sus componentes (y los de éstos) debajo
    if (item.from.length > 0) {
      parts.push(h('h4', undefined, t('Receta')));
      parts.push(this.recipeTree(id, [...player.items]));
      parts.push(h('p', 'detail-note', t('Combinar: {gold} de oro', { gold: item.combine })));
    }
    if (item.into.length > 0) {
      parts.push(h('h4', undefined, t('Se usa en')));
      const into = h('div', 'detail-into');
      for (const next of item.into) into.append(this.chip(next, player.items.includes(next)));
      parts.push(into);
    }

    const discounted = plan.cost < item.cost;
    const buy = h('button', 'btn btn-primary btn-block detail-buy');
    buy.type = 'button';
    buy.innerHTML = `<span class="coin" aria-hidden="true"></span>${plan.cost}${discounted ? ` <s>${item.cost}</s>` : ''}`;
    buy.disabled = !canShop || Boolean(plan.blocked) || player.gold < plan.cost;
    buy.title = !canShop
      ? t('Sólo se compra en tu base')
      : plan.blocked === 'unique'
        ? t('Ya tienes este objeto')
        : plan.blocked === 'group'
          ? t('Ya llevas unas botas')
          : plan.blocked === 'slots'
            ? t('Inventario lleno')
            : player.gold < plan.cost
              ? t('Te faltan {gold} de oro', { gold: plan.cost - player.gold })
              : t('Comprar {item}', { item: t(item.name) });
    buy.addEventListener('click', () => this.onBuy(id));
    const foot = h('div', 'detail-foot');
    foot.append(buy, h('div', 'detail-hint', buy.title));
    parts.push(foot);
    this.detail.replaceChildren(...parts);
  }

  /** Árbol de la receta; marca (✓) los componentes que el inventario cubre. */
  private recipeTree(id: string, available: string[]): HTMLElement {
    const node = h('div', 'recipe-node');
    const children = h('div', 'recipe-children');
    for (const part of ITEMS[id].from) {
      const at = available.indexOf(part);
      const owned = at >= 0;
      if (owned) available.splice(at, 1);
      const branch = h('div', 'recipe-branch');
      branch.append(this.chip(part, owned));
      // si no lo tiene, se ve de qué está hecho (y qué parte ya tiene)
      if (!owned && ITEMS[part].from.length > 0) branch.append(this.recipeTree(part, available));
      children.append(branch);
    }
    node.append(children);
    return node;
  }

  private chip(id: string, owned: boolean): HTMLElement {
    const chip = h('button', `recipe-chip${owned ? ' owned' : ''}`);
    chip.type = 'button';
    const icon = h('span', 'item-icon');
    itemIcon(id, icon);
    chip.append(icon, h('span', 'chip-price', owned ? '✓' : String(ITEMS[id].cost)));
    chip.addEventListener('click', () => this.select(id));
    chip.addEventListener('contextmenu', (event) => {
      event.preventDefault();
      this.onBuy(id);
    });
    return chip;
  }
}
